import { byBacklogRank, TIME_HORIZON_ORDER } from "@pilotfish/core";
import { useCallback, useEffect, useState } from "react";
import {
  type DeleteItemEnvelope,
  fetchBacklogPage,
  type ItemPatch,
  type PlateItem,
  proposeDelete,
  proposeResolve,
  proposeUpdate,
} from "../api/backlog";
import { type AppApi, AppRequestError } from "../api/client";
import { fetchRoster, type RosterMember } from "../api/roster";
import { confirmWrite, newIdempotencyKey, type WriteEnvelope } from "../api/writes";
import type { ChipValue } from "../components/Chips";
import { Confirm } from "../components/Confirm";
import { ItemForm } from "../components/ItemForm";
import {
  ANYONE,
  OPEN,
  PlateFilters,
  type Showing,
  showingMatches,
  whoseAdmitsPatch,
  whoseItem,
} from "../components/PlateFilters";
import { Screen } from "../components/Screen";
import { ErrorState, Loading } from "../components/states";
import { HORIZON_HEADING, isHorizon } from "../horizons";
import { usePanel } from "../panelContext";

/**
 * **What's on your plate** — the family's list on the web (issue #310, ADR 0021 decision 5).
 *
 * The section that closes the panel: every family page can now show, correct and remove what
 * it holds. It is also the one most at risk of changing what the product *feels* like, which
 * is why the controls this screen deliberately does not have are as load-bearing as the three
 * it does.
 *
 * ## Three acts, named as three things
 *
 * *Done* is `resolve`. *Edit* is `update`. *Forget* is `delete_item` (#309). Not something like
 * them — the same plain functions reached through #298's routes, with the same tier, the same
 * `PROPOSAL#` and the same `ACTION#`. Collapsing any two is how somebody loses what they meant
 * to keep or keeps what they meant to lose: closing an item keeps the row, its title and the
 * family's own words in the list's history and in the export, and until #309 that was the only
 * lever there was.
 *
 * This component decides **when to ask the server** and **what to render**, and decides nothing
 * about what a write means. It never predicts a tier: a write is sent, the answer says
 * `applied` or `proposed`, and a proposal opens the confirmation.
 *
 * ## What is not here, and why the absence is the design
 *
 * - **No *＋ New*.** Items arrive from what the family says. Classifying an utterance is the
 *   host model's job and the server calls no completions API, so a panel could only do it by
 *   making the family fill in a form — the thing this product exists not to be. `writes.ts`
 *   does not name `/capture`, so this file could not send one.
 * - **No drag, no reorder, no manual ranking.** Grouping is the stored `timeHorizon`, and the
 *   order inside a group is `get_backlog`'s own (`byBacklogRank`, shared through
 *   `@pilotfish/core` precisely so the two surfaces cannot drift).
 * - **No saved views, no bulk select, no board.**
 *
 * ## The screen reads the whole list, and that is what lets it filter
 *
 * `GET /backlog` is paged and newest-first. Every claim this screen makes — *Open*, *Finished*,
 * a count beside a heading — is a claim about the **whole** list, and none of them can be made
 * from page one: a family with sixty items would be shown "Finished: 3" meaning *three among
 * the newest fifty*. So the load walks the cursor to the end, exactly as the memory screen does
 * (#329), and every count is conditional on {@link Load.truncated} — past {@link MAX_ITEMS} the
 * counts disappear and the screen says what it is showing and where the rest is.
 *
 * The filters are then pure presentation: no request gains a parameter and the same bytes are
 * fetched whichever chips are lit.
 *
 * ## A closed item offers *Edit* and *Forget*, but not *Done*
 *
 * `resolve` reads the item through `getOpenItem`, which throws `ItemNotOpenError` for one that
 * is already closed — so *Done* on a finished row would be a control that cannot work.
 *
 * ***Edit* was hidden with it and should not have been** (issue #83). `update` inherited that
 * gate without the reasoning being re-argued, and the reasoning does not carry: correcting the
 * wording of something finished is not nonsense, it is *when* a family notices. Their only
 * lever was `delete_item` — destroy the record to fix its wording — which is the same dead end
 * #306 removed for a durable fact. `update` reads through `getItem` now, and every title on
 * this screen is editable.
 *
 * `delete_item` never required an open item (#309), because *"it shouldn't be here at all"* is
 * just as true of something closed out. **None of this is a hidden capability**: no role is
 * consulted anywhere on this screen, every member may call all three tools, and a refusal the
 * server makes is rendered in the server's own words.
 */
export function Plate({ familyId }: { familyId: string }) {
  const { api } = usePanel();

  const [load, setLoad] = useState<Load>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  const [editing, setEditing] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [confirming, setConfirming] = useState<Confirming | null>(null);
  const [showing, setShowing] = useState<Showing>(OPEN);
  const [whose, setWhose] = useState<ChipValue>(ANYONE);
  /**
   * The control to hand focus back to once the form closes.
   *
   * A key rather than a node, because the trigger is *conditionally rendered*: opening the form
   * unmounts it and cancelling mounts a **new** element, so a captured reference would point at
   * a node that is no longer in the document.
   */
  const [restoreFocusTo, setRestoreFocusTo] = useState<string | null>(null);

  const closeForm = useCallback((focusKey: string | null) => {
    setEditing(null);
    setRestoreFocusTo(focusKey);
  }, []);

  useEffect(() => {
    if (editing !== null || restoreFocusTo === null) return;
    const target = document.querySelector<HTMLElement>(`[data-focus-key="${restoreFocusTo}"]`);
    target?.focus();
    setRestoreFocusTo(null);
  }, [editing, restoreFocusTo]);

  // `attempt` is not read in the effect — it IS the trigger, the shell's own idiom. Every write
  // that applies calls `reload()`, and the inputs it would otherwise depend on are unchanged by
  // definition: the same family, the same api.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` is the re-run trigger.
  useEffect(() => {
    let live = true;
    setLoad({ status: "loading" });
    Promise.all([readWholeList(api, familyId), fetchRoster(api, familyId)])
      .then(([list, roster]) => {
        if (!live) return;
        setLoad({
          status: "ready",
          items: list.items,
          truncated: list.truncated,
          members: roster.members,
        });
      })
      .catch((error: unknown) => {
        if (live) setLoad({ status: "failed", detail: detailOf(error) });
      });
    return () => {
      live = false;
    };
  }, [api, familyId, attempt]);

  /**
   * Re-read the list **without taking the screen down**.
   *
   * `reload()` sets `status: "loading"`, which swaps the whole tree for a spinner and takes an
   * open form — and whatever somebody had typed into it — with it. That is fine after an applied
   * write, where the form is deliberately closed anyway, and destructive on the one path where
   * it matters most: a transport failure while writing, where the write may also have landed.
   *
   * **It reports whether it worked, and the caller has to care** (PR #336 review, finding 4).
   * An earlier version swallowed the failure while the copy above it said *"your list has been
   * re-read below"* regardless — during the same outage that loses a write's response these two
   * GETs fail too, so a family could be shown stale rows under a sentence claiming they were
   * reconciled, and decide whether to retry an irreversible write on the strength of it.
   *
   * It still throws nothing: a failed background read is a fact the caller folds into the one
   * sentence it is already writing, not a second contradicting message.
   */
  const refresh = useCallback(async (): Promise<boolean> => {
    try {
      const [list, roster] = await Promise.all([
        readWholeList(api, familyId),
        fetchRoster(api, familyId),
      ]);
      setLoad((current) =>
        current.status === "ready"
          ? {
              ...current,
              items: list.items,
              truncated: list.truncated,
              members: roster.members,
            }
          : current,
      );
      return true;
    } catch {
      return false;
    }
  }, [api, familyId]);

  /**
   * Send a write, and route its answer.
   *
   * The whole of this component's write logic, shared by all three acts, because the branch is
   * the *envelope's* and not the operation's: `proposed` opens the confirmation, `applied`
   * closes the form and re-reads. A refusal shows the server's own sentence where it gave one.
   */
  const send = useCallback(
    async <T extends WriteEnvelope>(
      run: () => Promise<T>,
      tail: PlateTail,
      at: RefusalAt,
      /**
       * Clear the *Whose* chip once this lands — see {@link saveEdit}.
       *
       * Carried through the proposal rather than acted on at submit, because a family who
       * cancels the confirmation has changed nothing and should be looking at exactly what
       * they were looking at before.
       */
      reveal = false,
    ): Promise<void> => {
      setPending(true);
      setRefusal(null);
      try {
        const envelope = await run();
        if (envelope.status === "proposed") {
          // A proposal with no id cannot be confirmed, and falling through would close the
          // form, clear the dialog and re-read — this screen's entire vocabulary for "that
          // worked", said about a write that did not happen. #298 returns an id with every
          // proposal, so this is unreachable; it is handled because the failure mode is the
          // one thing this screen may never do.
          if (envelope.proposalId === null) {
            setRefusal({ detail: GENERIC_WRITE_FAILURE, at });
            return;
          }
          setConfirming({ envelope, tail, proposalId: envelope.proposalId, reveal });
          return;
        }
        setEditing(null);
        setConfirming(null);
        // **The write landed somewhere the filter is hiding** — see {@link saveEdit}.
        if (reveal) setWhose(ANYONE);
        // **`refresh`, not `reload`** (PR #336 review, finding 4). `reload()` swaps the whole
        // tree for a spinner, which on this screen costs the scroll position — and marking
        // several things off in a row is the single most likely reason a family opens it, so
        // every *Done* two thirds of the way down a long list sent them back to the top. The
        // form is closed on the line above either way, so unmounting preserves nothing.
        // `reload()` stays for the initial read and its retry, where there is genuinely
        // nothing on screen to keep.
        if (!(await refresh())) {
          // **`page`, never `at`** — by here the form is closed, so a refusal addressed to it
          // would render nowhere at all.
          setRefusal({ detail: APPLIED_BUT_UNREAD, at: "page" });
        }
      } catch (error: unknown) {
        const detail = detailOf(error);
        if (detail !== null) {
          setRefusal({ detail, at });
        } else if (wasAnswered(error)) {
          // The server answered and refused, so the write did not happen and saying so is true.
          setRefusal({ detail: GENERIC_WRITE_FAILURE, at });
        } else {
          // **Nothing answered, so the write may well have committed.** Say so at once — during
          // the outage this describes the re-read below can hang for as long as the browser's
          // own timeout, and a family left with no message and a *Saving…* button is worse off
          // than one told immediately. So the first sentence claims nothing about the re-read,
          // and the second replaces it only if that read fails.
          setRefusal({ detail: AMBIGUOUS_WRITE_FAILURE, at });
          void refresh().then((reconciled) => {
            if (reconciled) return;
            // **Only if it is still the sentence we wrote.** A newer write clears the refusal
            // and may set its own, and a late answer about an abandoned one would overwrite it.
            setRefusal((current) =>
              current !== null && current.detail === AMBIGUOUS_WRITE_FAILURE
                ? { detail: AMBIGUOUS_AND_UNREAD, at: current.at }
                : current,
            );
          });
        }
      } finally {
        setPending(false);
      }
    },
    [refresh],
  );

  const markDone = (item: PlateItem) =>
    void send(
      () => proposeResolve(api, familyId, item.itemId, newIdempotencyKey()),
      "/resolve",
      "page",
    );

  const saveEdit = (item: PlateItem, patch: ItemPatch) =>
    void send(
      () => proposeUpdate(api, familyId, item.itemId, patch, newIdempotencyKey()),
      "/update",
      "form",
      // **Whether the correction will land where the family can still see it** (PR #336
      // review, finding 3). Filtered to *Dana's* and handing an item to Sam is a perfectly
      // ordinary edit — and an applied write relays no message on this screen, so the only
      // visible outcome would be the row they just corrected disappearing. Nothing about the
      // write is wrong; the view is.
      !whoseAdmitsPatch(item, patch, whose),
    );

  const forget = (item: PlateItem) =>
    void send(
      () => proposeDelete(api, familyId, item.itemId, newIdempotencyKey()),
      "/delete-item",
      "page",
    );

  const confirm = () => {
    if (confirming === null) return;
    const { tail, proposalId, reveal } = confirming;
    void send(() => confirmWrite(api, familyId, tail, proposalId), tail, "dialog", reveal);
  };

  if (load.status === "loading") {
    return (
      <Screen title={TITLE} blurb={OPENING}>
        <Loading label="Reading your family's list…" />
      </Screen>
    );
  }

  if (load.status === "failed") {
    return (
      <Screen title={TITLE} blurb={OPENING}>
        <ErrorState
          title="We couldn't read your family's list"
          body={load.detail ?? GENERIC_READ_FAILURE}
        />
        {/*
          **The one place `reload()` still belongs** (PR #336 review, finding 4). Every applied
          write now re-reads through `refresh`, which keeps the rows and the scroll position —
          so the whole-tree reload is left for the two states where there is nothing on screen
          to preserve: the first read, and this retry of it. A trigger nothing pulls is a
          dependency somebody later has to work out (`Activity.tsx` says so), and a failed read
          with no way back was the other half of that.
        */}
        <button className="cta" type="button" onClick={reload}>
          Try again
        </button>
      </Screen>
    );
  }

  // Read once per render so every row on the page is judged against the same instant.
  const today = localToday();
  // A proposal is open: every control that could start a *second* write is out of bounds until
  // it is answered. Without this, the page behind the scrim stays live — which is how Shift+Tab
  // reached a delete control and swapped the proposal under the dialog (Confirm.tsx).
  const locked = pending || confirming !== null;
  // Whether the list on screen is all of it — the condition on every count below.
  const complete = !load.truncated;
  const shown = load.items.filter(
    (item) => showingMatches(item, showing) && whoseItem(item, whose),
  );
  const groups = groupByHorizon(shown);
  /**
   * **Nothing to show, on a list that is not empty** — and the two reasons read differently
   * (PR #336 review, finding 1).
   *
   * The guard used to be *"a filter is set"* (`showing !== OPEN || whose !== ANYONE`), which is
   * `Memory.tsx`'s shape and is wrong here for one reason: that screen's default chip is
   * *Everyone's*, which admits everything, so "no filter" really does imply "everything is on
   * screen". **This screen's default chip excludes** — `showingMatches(item, "open")` drops
   * every `done` and `dropped` row — so a family who had finished everything hit all three
   * empty branches at once and got the title, the chips and the two notes with a hole between
   * them, and no sentence saying why.
   *
   * So the guard is the thing actually being asked (nothing is rendered, but they do have a
   * list) and the *copy* branches. The finished-everything sentence is also the nicer thing to
   * land on: it is the state this screen most wants to be able to say out loud.
   */
  const nothingShown = shown.length === 0 && load.items.length > 0;
  const defaultView = showing === OPEN && whose === ANYONE;

  return (
    <Screen
      title={TITLE}
      blurb={OPENING}
      aside={
        <PlateFilters
          showing={showing}
          whose={whose}
          members={load.members}
          /*
            **The chips wait for an open form.** A filter change unmounts every row it excludes
            — React reconciles them by `itemId` — and one of those rows may hold a half-typed
            correction. Finishing or cancelling costs one click.
          */
          disabled={editing !== null || locked}
          onShowing={setShowing}
          onWhose={setWhose}
        />
      }
    >
      {/*
        A refusal that belongs to the page rather than to the open form or the dialog. *Done*
        and *Forget* are both first calls, so a refusal arrives with no form and no dialog to
        put it in. `at` is stated by whoever sent the write, so a delete refused while an
        unrelated edit form happens to be open no longer renders inside it.
      */}
      {refusal !== null && refusal.at === "page" ? (
        <div className="memory__refusal" role="alert">
          <p>{refusal.detail}</p>
        </div>
      ) : null}

      {/*
        **The list is genuinely empty**, which is different from a filter matching nothing and
        is worth its own words. The two notes below still render underneath, because on this
        screen the thing a family most needs to know when the page is blank is that there is no
        button to press — items arrive from what they say.
      */}
      {complete && load.items.length === 0 ? (
        <div className="memory__nothing">
          <h2 className="memory__nothing-title">Nothing on your plate</h2>
          <p className="memory__nothing-body">
            This is genuinely empty, not hidden. Things land here when one of you mentions them —
            tell <strong>@boone</strong> something like{" "}
            <em>"we need to book the dentist for Maya"</em> in your usual chat, and it'll show up on
            this list.
          </p>
        </div>
      ) : null}

      {groups.map((group) => (
        <section className="plate__group" key={group.key}>
          <header className="plate__group-head">
            <h2 className="plate__heading">{group.heading}</h2>
            {/* A count is a claim, so it is only made about a list we have all of. */}
            {complete ? <span className="plate__count">{group.items.length}</span> : null}
          </header>
          <ul className="plate__list">
            {group.items.map((item) => (
              <ItemRow
                key={item.itemId}
                item={item}
                today={today}
                members={load.members}
                editing={editing === item.itemId}
                pending={pending}
                locked={locked}
                draftOpen={editing !== null}
                error={refusal?.at === "form" ? refusal.detail : null}
                onEdit={() => {
                  setRefusal(null);
                  setEditing(item.itemId);
                }}
                onCancel={() => closeForm(`edit-${item.itemId}`)}
                onSave={(patch) => saveEdit(item, patch)}
                onDone={() => markDone(item)}
                onForget={() => forget(item)}
              />
            ))}
          </ul>
        </section>
      ))}

      {/* Neither sentence ever says the list is empty — that is the block above, and it is a
          different fact. See {@link nothingShown}. */}
      {nothingShown ? (
        <p className="memory__empty">
          {defaultView
            ? 'Nothing open right now — everything on your family\'s list is finished. Try "Finished" or "Everything" to see it.'
            : 'Nothing on the list matches those. Try "Open" and "Anyone\'s".'}
        </p>
      ) : null}

      {/* The one stated limit. See {@link MAX_ITEMS}: it is a bound nobody has met, and the
          honest thing on the day somebody does is to say what is on screen and where the rest
          is, rather than to show a pager the rest of this screen is designed without. */}
      {complete ? null : (
        <div className="memory__truncated">
          <p>
            This is the first {load.items.length.toLocaleString()} things on your family's list, and
            there are more. Ask <strong>@boone</strong> in Claude or ChatGPT for the rest.
          </p>
        </div>
      )}

      {/*
        The canvas's two notes, and they are not decoration. The first is the whole reason there
        are three controls instead of one: *Done*, *Edit* and *Forget* do different things to
        the family's own words, and the row cannot say so in three link labels. The second
        answers the question every list screen invites — *"where do I add one?"* — before
        somebody goes looking for a button that is deliberately absent.
      */}
      <div className="plate__notes">
        <div className="plate__note">
          <h2 className="plate__note-title">Done, Edit and Forget are three different things</h2>
          <p className="plate__note-line">
            <strong>Done</strong> — it happened. It leaves this list and stays in the record.
          </p>
          <p className="plate__note-line">
            <strong>Edit</strong> — Boone got it slightly wrong. The words you said stay underneath,
            unchanged.
          </p>
          <p className="plate__note-line">
            <strong>Forget</strong> — it shouldn't be here at all. It goes, and so do the words you
            said.
          </p>
        </div>
        <div className="plate__note plate__note--quiet">
          <h2 className="plate__note-title">Nothing here is added by hand</h2>
          <p className="plate__note-body">
            There is no "＋ New" on this page. Things arrive here from what the family says to
            Boone; this screen is for checking, fixing and removing them.
          </p>
        </div>
      </div>

      {confirming === null ? null : (
        <Confirm
          message={confirming.envelope.message}
          disclosures={doesNotReach(confirming.envelope)}
          pending={pending}
          error={refusal?.at === "dialog" ? refusal.detail : null}
          onConfirm={confirm}
          onCancel={() => {
            setConfirming(null);
            setRefusal(null);
          }}
        />
      )}
    </Screen>
  );
}

/** One thing on the list, and the three things a family can do to it. */
function ItemRow({
  item,
  today,
  members,
  editing,
  pending,
  locked,
  draftOpen,
  error,
  onEdit,
  onCancel,
  onSave,
  onDone,
  onForget,
}: {
  item: PlateItem;
  /** The reader's own calendar date — see {@link dueLabel}. */
  today: string;
  members: readonly RosterMember[];
  editing: boolean;
  pending: boolean;
  /** A write is in flight or a proposal is open — nothing here may start a second one. */
  locked: boolean;
  /**
   * **Some row on this page has a half-typed correction in it** (PR #336 review, finding 3).
   *
   * The chips already wait for this and the reason given beside them covers these controls *a
   * fortiori*: a filter change only re-renders, while these also **write**. With an edit form
   * open on row X, *Done* or *Forget* on row Y would apply and then run `setEditing(null)`
   * unconditionally — the form on X and everything typed into it gone, with no warning and
   * nothing to recover them. Under the default `ask_always` that lands two interactions after
   * the point of no return, about a row nobody was looking at.
   *
   * The edited row itself renders the form instead of these controls, so it is unaffected.
   * *"Finish or cancel that first."* is already on the page, under the frozen chips.
   */
  draftOpen: boolean;
  error: string | null;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (patch: ItemPatch) => void;
  onDone: () => void;
  onForget: () => void;
}) {
  const closed = item.status !== "open";
  const due = dueLabel(item.dueDate, today);
  return (
    <li className="plate__item">
      <div className="plate__body">
        <h3 className="plate__title">{item.title}</h3>
        <div className="plate__chips">
          {/*
            **No owner shows as *nobody's yet*, never as an id** — and it covers two situations
            the screen must not distinguish: nobody was ever down for it, and the person who was
            has left. `getRoster` is active-only and ADR 0019 decision 5 keeps a departed
            member's attribution on the record, so an unresolvable id is an ordinary state
            rather than a fault, and the server already resolves it to `null` for us.
          */}
          {item.owner === null ? (
            <span className="plate__chip plate__chip--none">nobody's yet</span>
          ) : (
            <span className="plate__chip">{item.owner}'s</span>
          )}
          {due === null ? null : (
            <span className={due.overdue ? "plate__chip plate__chip--late" : "plate__chip"}>
              {due.label}
            </span>
          )}
          {/* Under *Finished* and *Everything* a closed row has to be visibly closed, or the
              list is telling a family something untrue about their own record. */}
          {closed ? <span className="plate__chip plate__chip--done">{item.status}</span> : null}
        </div>
      </div>

      {editing ? null : (
        <div className="plate__actions">
          {/*
            **Only *Done* is hidden on a closed row** — see the component header. `resolve`
            reads through `getOpenItem` and throws `ItemNotOpenError`, so closing something
            twice would be a control that cannot work. *Edit* used to be hidden with it and
            should not have been (issue #83): correcting the wording of something finished is
            when a family usually notices, and their only lever was to delete the record to fix
            it. `update` reads through `getItem` now. No role is consulted here or anywhere on
            this screen.
          */}
          {closed ? null : (
            <button
              className="linklike linklike--primary"
              type="button"
              onClick={onDone}
              disabled={locked || draftOpen}
            >
              Done
            </button>
          )}
          <button
            className="linklike"
            type="button"
            data-focus-key={`edit-${item.itemId}`}
            onClick={onEdit}
            disabled={locked || draftOpen}
          >
            Edit
          </button>
          <button
            className="linklike linklike--destructive"
            type="button"
            onClick={onForget}
            disabled={locked || draftOpen}
          >
            Forget
          </button>
        </div>
      )}

      {editing ? (
        <ItemForm
          item={item}
          members={members}
          pending={pending}
          locked={locked}
          error={error}
          onSubmit={onSave}
          onCancel={onCancel}
        />
      ) : null}
    </li>
  );
}

const TITLE = "What's on your plate";
/** `Backlog.dc.html`'s opening paragraph, verbatim. */
const OPENING =
  "What the family has asked Boone to keep track of. Boone puts things here from what you say — this is where you check it, fix it, or take something back.";

/**
 * How much of the list the walk will read before it stops and says so.
 *
 * A thousand items — an order of magnitude past anything a family has, and the point is that
 * the bound *exists* rather than what it is. An unbounded loop over a paged read is one slow
 * morning away from a browser making a hundred requests for a screen nobody can read anyway.
 *
 * **Counted in records rather than in pages** (PR #336 review, finding 5). A page bound says a
 * different thing depending on what a page turns out to hold: the previous version was
 * `MAX_PAGES = 20` with a comment claiming `MAX_PAGE_SIZE` was 50, and it is 200 —
 * `DEFAULT_PAGE_SIZE` is the 50 the arithmetic actually relied on, and only because the walk
 * sent no `?limit=`. Now that it asks for full pages, a page bound would be worse still: the
 * server may return a **short** page whenever DynamoDB stops at 1 MB, so `MAX_PAGES = 5` would
 * silently lower the number of items a family is guaranteed to see. A record count is exact
 * whatever a page holds.
 */
const MAX_ITEMS = 1_000;

/**
 * How many to ask for per request — `MAX_PAGE_SIZE`, the ceiling the contract allows.
 *
 * The walk sent no `?limit=` and got `DEFAULT_PAGE_SIZE`, so reading a long list was twenty
 * sequential round trips where five would do — each one a Lambda invoke behind CloudFront, on
 * the one screen whose whole design is *read all of it before rendering anything*. A short page
 * is not a lost record: the cursor is followed either way.
 */
const PAGE_SIZE = 200;

const GENERIC_READ_FAILURE =
  "Something went wrong at our end. Try again in a moment — nothing you've told Boone is affected.";
const GENERIC_WRITE_FAILURE =
  "Something went wrong at our end, and nothing was changed. Try again in a moment.";
/**
 * What to say when we genuinely do not know whether the write landed.
 *
 * `appFetch` rejects for two different things — a refusal the server sent, and a request whose
 * response never came back. The second covers a write that committed, including a confirmed
 * `delete_item`, and telling that family *"nothing was changed"* is a confident falsehood about
 * something irreversible. Retrying does not recover the answer either: each attempt mints a
 * fresh `idempotencyKey`, so the client cannot tell a replay from a second write.
 */
const AMBIGUOUS_WRITE_FAILURE =
  "We couldn't tell whether that went through, so nothing here is certain yet. Check the list before trying again.";
/**
 * The same, when the re-read did not come back either.
 *
 * The sentence above is a **claim about what is on screen**, and during the outage that lost
 * the write's response the two GETs behind it fail too. Saying it anyway would leave a family
 * deciding whether to retry an irreversible write on the strength of rows that were never
 * refreshed.
 */
const AMBIGUOUS_AND_UNREAD =
  "We couldn't tell whether that went through, and we couldn't re-read your list either. Give it a moment and check the list before trying again.";
/** The write landed and the re-read after it did not. What is below is a moment out of date. */
const APPLIED_BUT_UNREAD =
  "That went through. We couldn't refresh the list just now, so what's below may be a moment behind — reload the page to catch up.";

/** The three routes this screen writes through — a narrowing of `writes.ts`'s `WriteTail`. */
type PlateTail = "/resolve" | "/update" | "/delete-item";

type Load =
  | { status: "loading" }
  | { status: "failed"; detail: string | null }
  | {
      status: "ready";
      items: readonly PlateItem[];
      /** The walk hit {@link MAX_ITEMS} — every count on the screen is off. */
      truncated: boolean;
      members: readonly RosterMember[];
    };

/**
 * Where a refusal belongs on the screen.
 *
 * **Stated by whoever sent the write, never inferred from what happens to be open.** One string
 * shared by three renderers, shown wherever "no form and no dialog" happened to be true, would
 * put a delete's refusal inside an unrelated open edit form — attached to a write the family
 * did not make, on a row it had nothing to do with.
 */
type RefusalAt = "form" | "dialog" | "page";

interface Refusal {
  readonly detail: string;
  readonly at: RefusalAt;
}

interface Confirming {
  envelope: WriteEnvelope;
  tail: PlateTail;
  proposalId: string;
  /** Whether applying this should clear the filter — see `send`'s parameter of the same name. */
  reveal: boolean;
}

/**
 * Read the family's whole list, following the cursor to the end.
 *
 * Bounded by {@link MAX_ITEMS} and honest about it: `truncated` is what every count on the
 * screen hangs off. A plain loop rather than anything cleverer, because the pages are strictly
 * ordered and each cursor comes from the answer before it.
 */
async function readWholeList(
  api: AppApi,
  familyId: string,
): Promise<{ items: PlateItem[]; truncated: boolean }> {
  const items: PlateItem[] = [];
  let cursor: string | null = null;
  do {
    const page = await fetchBacklogPage(api, familyId, cursor, PAGE_SIZE);
    items.push(...page.records);
    cursor = page.nextCursor;
  } while (cursor !== null && items.length < MAX_ITEMS);
  return { items, truncated: cursor !== null };
}

interface Group {
  /** React's key, and the raw horizon for one this build has no words for. */
  readonly key: string;
  readonly heading: string;
  readonly items: readonly PlateItem[];
}

/**
 * The list, grouped by its **stored** horizon and ranked inside each group.
 *
 * No clock is consulted to bucket anything (ADR 0006 decision 9) and no group anybody has to
 * maintain: the three buckets in `@pilotfish/core`'s order, then `byBacklogRank` — which is
 * `get_backlog`'s own comparator, shared rather than re-implemented so the list a family reads
 * in Claude and the list they read here cannot come back in two orders.
 *
 * **An empty group is dropped, and a heading this build has never heard of is kept.** The
 * canvas draws only the groups that have something in them, and a heading with a zero beside it
 * on a screen whose whole job is not feeling like a board is exactly the wrong furniture. A
 * *fourth* horizon added server-side is the opposite case: dropping those items would make a
 * screen that counts every group quietly incomplete, so they are grouped under their raw value
 * and stay readable, correctable and deletable.
 */
function groupByHorizon(items: readonly PlateItem[]): readonly Group[] {
  const buckets = new Map<string, PlateItem[]>();
  for (const item of items) {
    const found = buckets.get(item.timeHorizon);
    if (found === undefined) buckets.set(item.timeHorizon, [item]);
    else found.push(item);
  }

  const groups: Group[] = [];
  for (const horizon of TIME_HORIZON_ORDER) {
    const bucket = buckets.get(horizon);
    if (bucket === undefined || bucket.length === 0) continue;
    groups.push({ key: horizon, heading: HORIZON_HEADING[horizon], items: ranked(bucket) });
  }
  // Whatever is left is a horizon this build has never heard of. Sorted by its raw value so
  // two of them come back in a stable order rather than in insertion order, which is the read's
  // and would reshuffle when an item is added.
  const strangers = [...buckets].filter(([horizon]) => !isHorizon(horizon));
  for (const [horizon, bucket] of strangers.sort(([a], [b]) => a.localeCompare(b))) {
    groups.push({ key: horizon, heading: horizon, items: ranked(bucket) });
  }
  return groups;
}

/**
 * One group, in the tool's order.
 *
 * `priority` is a plain string on this wire (`disclosure.ts` projects the stored value), and
 * `byBacklogRank` reads it as one of three. A value this build has never seen would index the
 * rank table as `undefined`, so it is pinned to the middle — a row that sorts sensibly rather
 * than one that lands wherever `NaN` puts it.
 */
function ranked(items: readonly PlateItem[]): readonly PlateItem[] {
  return [...items].sort((a, b) =>
    byBacklogRank(
      { priority: priorityOf(a), dueDate: a.dueDate, createdAt: a.createdAt },
      { priority: priorityOf(b), dueDate: b.dueDate, createdAt: b.createdAt },
    ),
  );
}

function priorityOf(item: PlateItem): "low" | "medium" | "high" {
  return item.priority === "high" || item.priority === "low" ? item.priority : "medium";
}

/**
 * `now` as a `YYYY-MM-DD` calendar date **in the reader's own zone**.
 *
 * `get_backlog` uses the family's `META.defaultTimezone` instead, and the difference is
 * deliberate rather than an oversight. A model has no clock, so the family's zone is the only
 * sensible one for it; a browser has the reader's, and *"is this due today"* asked on a screen
 * is a question about the person reading it. They can disagree by a day, near midnight, for a
 * member travelling — which is the smaller error of the two available.
 */
function localToday(): string {
  // en-CA formats as ISO `YYYY-MM-DD`, the same trick `shaping.ts` uses.
  return new Intl.DateTimeFormat("en-CA").format(new Date());
}

/**
 * The due chip — `Backlog.dc.html`'s *by Friday*, and the states around it.
 *
 * A weekday inside the coming week, because that is how a family says it; a date past that,
 * because *"by Tuesday"* three weeks out is a worse answer than the date. Overdue is called
 * out, and it is the only one of these that is a *judgement* rather than a formatting choice —
 * an item whose date has gone is the thing on this list most likely to be why somebody opened
 * the page.
 *
 * A `dueDate` that is not a date is rendered as it arrived rather than as `Invalid Date`. It
 * cannot happen through the tools (`ISO_DATE` is enforced at the schema), and rendering the raw
 * string is the honest failure.
 */
function dueLabel(
  dueDate: string | null,
  today: string,
): { label: string; overdue: boolean } | null {
  if (dueDate === null || dueDate === "") return null;
  if (dueDate < today) return { label: "overdue", overdue: true };
  if (dueDate === today) return { label: "due today", overdue: false };

  const at = new Date(`${dueDate}T00:00:00`);
  if (Number.isNaN(at.getTime())) return { label: `by ${dueDate}`, overdue: false };

  const from = new Date(`${today}T00:00:00`);
  const days = Math.round((at.getTime() - from.getTime()) / DAY_MS);
  if (days === 1) return { label: "by tomorrow", overdue: false };
  if (days <= 6) {
    return {
      label: `by ${new Intl.DateTimeFormat(undefined, { weekday: "long" }).format(at)}`,
      overdue: false,
    };
  }
  return {
    label: `by ${new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short" }).format(at)}`,
    overdue: false,
  };
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The server's `doesNotReach` lines, when the envelope carries them.
 *
 * Narrowed at the one place the panel meets it rather than typed into {@link Confirming},
 * because only `delete_item` has them — and a `Confirm` that took a union of envelopes would
 * make the dialog know which tool it is confirming, which is the one thing it must not.
 */
function doesNotReach(envelope: WriteEnvelope): readonly string[] {
  const { doesNotReach: lines } = envelope as Partial<DeleteItemEnvelope>;
  return Array.isArray(lines) ? lines : [];
}

/**
 * The server's sentence for a refusal, or `null` to fall back to a generic one.
 *
 * **`invalid_input` is deliberately dropped.** #298 answers a schema failure with
 * `z.prettifyError`, which is developer-facing by design — it names a field's path — and every
 * other refusal it relays is the tool's own family-facing words. A schema failure here is our
 * bug over a body the panel built, not something the family did, so the generic line is also
 * the true one.
 */
function detailOf(error: unknown): string | null {
  if (!(error instanceof AppRequestError)) return null;
  return error.code === "invalid_input" ? null : error.detail;
}

/**
 * Did the server answer at all?
 *
 * The one question two decisions turn on — what the family is told, and whether the list is
 * re-read — so it is asked once. `status === 0` is `appFetch`'s "nothing answered", and a throw
 * that is not an `AppRequestError` never reached the check at all.
 */
function wasAnswered(error: unknown): boolean {
  return error instanceof AppRequestError && error.status > 0;
}
